import React from 'react'
import { ThemeProvider as StyledThemeProvider } from 'styled-components'
import { ConfigProvider } from 'antd'
import { gray, turquoise, orange, semantic } from './colors'

export const theme = {
  // Color system
  colors: {
    // Raw color palettes
    gray,
    turquoise,
    orange,
    semantic,

    // Convenience properties for common use cases
    background: gray[50],
    backgroundSecondary: gray[100],
    backgroundElevated: '#ffffff',

    border: gray[200],
    borderSecondary: gray[300],
    borderBg: gray[50],

    text: gray[800],
    textSecondary: gray[600],
    textTertiary: gray[500],
    textQuaternary: gray[400],

    // Brand colors
    primary: turquoise[500],
    primaryHover: turquoise[400],
    primaryActive: turquoise[600],
    primaryBg: turquoise[50],

    secondary: orange[500],
    secondaryHover: orange[400],
    secondaryActive: orange[600],
    secondaryBg: orange[50],

    // Semantic colors
    success: semantic.success[500],
    successLight: semantic.success[100],
    successBg: semantic.success[50],

    warning: semantic.warning[500],
    warningLight: semantic.warning[100],
    warningBg: semantic.warning[50],

    error: semantic.error[500],
    errorLight: semantic.error[100],
    errorBg: semantic.error[50],

    info: semantic.info[500],
    infoLight: semantic.info[100],
    infoBg: semantic.info[50],
  },

  // Spacing system
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
    xxxl: '64px',
  },

  // Border radius system
  borderRadius: {
    none: '0',
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    full: '9999px',
  },

  // Shadow system
  shadows: {
    none: 'none',
    sm: '0 1px 2px rgba(0, 0, 0, 0.1)',
    md: '0 4px 6px rgba(0, 0, 0, 0.15)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.2)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.25)',
  },

  // Typography system
  typography: {
    fontFamily: {
      sans: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
      mono: 'Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    },
    fontSize: {
      xs: '12px',
      sm: '14px',
      base: '16px',
      lg: '18px',
      xl: '20px',
      '2xl': '24px',
      '3xl': '30px',
      '4xl': '36px',
    },
    fontWeight: {
      normal: '400',
      medium: '500',
      semibold: '600',
      bold: '700',
    },
    lineHeight: {
      tight: '1.25',
      normal: '1.5',
      relaxed: '1.75',
    },
  },

  // Breakpoint system
  breakpoints: {
    xs: '480px',
    sm: '576px',
    md: '768px',
    lg: '992px',
    xl: '1200px',
    xxl: '1600px',
  },

  // Z-index system
  zIndex: {
    hide: -1,
    auto: 'auto',
    base: 0,
    docked: 10,
    dropdown: 1000,
    sticky: 1100,
    banner: 1200,
    overlay: 1300,
    modal: 1400,
    popover: 1500,
    skipLink: 1600,
    toast: 1700,
    tooltip: 1800,
  },

  // Animation system
  animation: {
    duration: {
      fast: '150ms',
      normal: '250ms',
      slow: '350ms',
    },
    easing: {
      ease: 'ease',
      easeIn: 'ease-in',
      easeOut: 'ease-out',
      easeInOut: 'ease-in-out',
    },
  },
}

// Export spacing tokens directly for easy access
export const spacing = theme.spacing
export const borderRadius = theme.borderRadius
export const shadows = theme.shadows
export const colors = theme.colors
export const animation = theme.animation
export const typography = theme.typography

// Ant Design tokens derived from our theme
const antdTokens = {
  // Primary colors
  colorPrimary: theme.colors.primary,
  colorPrimaryHover: theme.colors.primaryHover,
  colorPrimaryActive: theme.colors.primaryActive,
  colorLink: theme.colors.primary,

  // Background colors
  colorBgBase: theme.colors.background,
  colorBgContainer: theme.colors.background,
  colorBgLayout: theme.colors.backgroundSecondary,
  colorBgElevated: theme.colors.backgroundElevated,
  colorBgSpotlight: gray[900],

  // Text colors
  colorText: theme.colors.text,
  colorTextSecondary: theme.colors.textSecondary,
  colorTextTertiary: theme.colors.textTertiary,
  colorTextQuaternary: theme.colors.textQuaternary,

  // Border colors
  colorBorder: theme.colors.border,
  colorBorderSecondary: theme.colors.borderSecondary,
  colorBorderBg: theme.colors.borderBg,

  // Status colors
  colorSuccess: theme.colors.success,
  colorWarning: theme.colors.warning,
  colorError: theme.colors.error,
  colorInfo: theme.colors.info,

  // Fill colors
  colorFill: gray[200],
  colorFillSecondary: gray[100],
  colorFillTertiary: gray[50],
  colorFillQuaternary: gray[50],
}

// TypeScript interface for the theme
export interface Theme {
  colors: typeof theme.colors
  spacing: typeof theme.spacing
  borderRadius: typeof theme.borderRadius
  shadows: typeof theme.shadows
  typography: typeof theme.typography
  breakpoints: typeof theme.breakpoints
  zIndex: typeof theme.zIndex
  animation: typeof theme.animation
}

interface ThemeProviderProps {
  children: React.ReactNode
}

/**
 * Unified ThemeProvider component
 * Provides both styled-components theme and Ant Design configuration
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  return (
    <ConfigProvider
      theme={{
        token: antdTokens,
      }}
    >
      <StyledThemeProvider theme={theme}>{children}</StyledThemeProvider>
    </ConfigProvider>
  )
}

export default ThemeProvider
